import React, { useState, useEffect } from 'react';
import { DocumentNode } from 'graphql';
import { isFuture } from 'date-fns';
import { pick } from 'lodash';
import moment from 'moment-timezone';
import uuid from 'uuid/v4';
import assertions from 'lib/assertions';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Checkbox from 'components/Checkbox';
import CoachingConstants from 'coaching/CoachingConstants';
import Container from 'components/Container';
import DatePickerInput from 'components/DatePickerInput';
import Divider from 'components/Divider';
import EditDayRow from 'coaching/components/CoachAvailabilityView/EditDayRow';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Input from 'components/Input';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';
import { dateOnlyFormat } from 'lib/dateTimeFormat';

import {
  AvailabilityScheduleInput,
  AvailabilityRuleInput,
  CoachAvailabilityInterval,
  DayOfWeek,
  MaxDailySessionsInput,
} from 'globalTypes';
import { CoachingAvailabilitySchedule } from 'coaching/containers/CoachAvailabilityContainer';
import { saveAvailabilitySchedule } from 'coaching/actions/CoachingActions';

export type Props = {
  initialTimezone: string;
  scheduleBeingEdited?: CoachingAvailabilitySchedule;
  onBack?: () => void;
  onSubmit?: () => void;
  availabilityQuery?: { query: DocumentNode; variables?: Object };
};

export default function AvailabilityScheduleEditView(props: Props) {
  const { initialTimezone, onBack, scheduleBeingEdited, onSubmit, availabilityQuery } = props;
  const scheduleIsInactive =
    !scheduleBeingEdited?.startDate || isFuture(scheduleBeingEdited.startDate);
  const timezoneOptions = TimezoneHelper.USTimezoneSelectOptions.filter(
    option => !CoachingConstants.TIMEZONES_TO_EXCLUDE.includes(option.label)
  );
  // we use the viewing timezone for interpreting the `DateOnly` string, because
  // the javascript Date object, which the `DatePickerInput` uses, always interprets
  // dates through the lens of the viewing timezone. the `DateOnly` string will get
  // interpreted correctly on the backend using the coach's actual timezone.
  const userViewingTimezone = moment.tz.guess();

  const [maxDailySessionsEnabled, setMaxDailySessionsEnabled] = useState(
    Boolean(scheduleBeingEdited?.maxDailySessions.length)
  );
  const [timezone, setTimezone] = useState<string>(
    timezoneOptions.some(o => o.value === initialTimezone)
      ? initialTimezone
      : TimezoneHelper.userTimezoneDefaultValue ?? timezoneOptions[0].value
  );
  const [scheduleInput, setScheduleInput] = useState<AvailabilityScheduleInput>(
    convertToInputType(scheduleBeingEdited) ?? {
      uuid: uuid(),
      label: '',
      startDate: '',
      availabilityRules: [],
      maxDailySessions: [],
    }
  );

  const [label, setLabel] = useState<string | undefined>(scheduleInput.label || undefined);
  const [startDate, setStartDate] = useState<string>(scheduleInput.startDate); // DateOnly formatted string OR an empty string
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (label && label.length > 0) {
      setScheduleInput({
        ...scheduleInput,
        label,
      });
    }
  }, [label]);

  useEffect(() => {
    if (startDate && startDate.length > 0) {
      setScheduleInput({
        ...scheduleInput,
        startDate,
      });
    }
  }, [startDate]);

  const updateScheduleWithNewIntervals = (
    dayOfWeek: DayOfWeek,
    newIntervals: CoachAvailabilityInterval[]
  ) => {
    setScheduleInput({
      ...scheduleInput,
      availabilityRules: [
        // retain rules for the other days of the week
        ...(scheduleInput?.availabilityRules.filter(r => r.dayOfWeek !== dayOfWeek) ?? []),
        // update rules for the specified day of week
        ...newIntervals.map(interval => ({
          uuid: uuid(),
          startTime: interval.startTime,
          endTime: interval.endTime,
          dayOfWeek,
        })),
      ],
    });
  };

  const updateScheduleWithNewMaxSessions = (dayOfWeek: DayOfWeek, maxSessions?: number) => {
    const maxDailySessions = scheduleInput?.maxDailySessions ?? [];
    const index = maxDailySessions.findIndex(m => m.dayOfWeek === dayOfWeek);
    if (maxSessions) {
      if (index > -1) {
        // remove the existing dayOfWeek input if it's there
        maxDailySessions.splice(index, 1);
      }
      // always push the new input
      maxDailySessions.push({
        maxSessions,
        dayOfWeek,
      });
    }
    setScheduleInput({
      ...scheduleInput,
      maxDailySessions,
    });
  };

  const saveNewSchedule = async () => {
    if (!timezone) {
      ToastActions.showErrorToast('Please select a valid timezone before saving your schedule.');
      return;
    }
    setSubmitting(true);
    await saveAvailabilitySchedule(
      {
        ...scheduleInput,
        maxDailySessions: maxDailySessionsEnabled ? scheduleInput.maxDailySessions : [],
      },
      timezone,
      availabilityQuery
    );
    setSubmitting(false);
    onSubmit?.();
  };

  return (
    <>
      {onBack && (
        <Row size="xsmall">
          <Button level="icon" color="chalkboard" onClick={onBack} data-test="back-arrow">
            <Icon name="arrow-left" />
          </Button>
        </Row>
      )}
      <Container size="xsmall">
        <Heading level="2">Set availability schedule</Heading>
        <Text element="p" lineHeight="large">
          Add the hours you’re available to book sessions. For one-offs like appointments or
          vacations, edit individual days later.
        </Text>
        <FormElement
          data-test="schedule-name-input"
          label="Schedule name"
          element={Input}
          elementProps={{
            placeholder: 'e.g. Fall hours or Basketball season',
            value: label,
          }}
          onValidChange={e => setLabel(e.target.value)}
          onInvalidChange={() => setLabel(undefined)}
          validate={e => {
            assertions.notEmpty(e.target.value, 'Your schedule must have a name.');
            assertions.lengthLesserThanOrEqualTo(
              e.target.value,
              60,
              'Schedule names can’t be longer than 60 characters.'
            );
          }}
        />
        <Divider size="medium" />
        {scheduleIsInactive && (
          <>
            <Heading level="4">When do you want your schedule to take effect?</Heading>
            <Text element="p" lineHeight="large">
              Your hours of availability will apply to all the dates starting on the effective date,
              except for manual edits.
            </Text>
            <FormElement
              data-test="schedule-start-date-input"
              label="Effective date"
              element={DatePickerInput}
              elementProps={{
                value: startDate ? moment.tz(startDate, userViewingTimezone).toDate() : undefined,
                full: true,
                min: new Date(),
              }}
              onValidChange={date => setStartDate(dateOnlyFormat(date, userViewingTimezone))}
              validate={date => {
                assertions.notEmpty(date, 'Your schedule must have a start date.');
              }}
            />
            <Divider size="medium" />
          </>
        )}
        <Heading level="4">What hours are you available?</Heading>
        <Text element="p" lineHeight="large">
          Tutoring is currently available to students 10:00 AM to 7:00 PM Pacific.
        </Text>
        <Row size="xlarge">
          <FormElement
            data-test="timezone-input"
            label="Time zone"
            element={Select}
            elementProps={{
              options: timezoneOptions,
              value: timezone,
              searchable: false,
              full: false,
            }}
            onValidChange={tz => setTimezone(tz)}
            validate={tz => {
              assertions.notEmpty(tz, 'You must select a valid timezone.');
            }}
          />
        </Row>
        {CoachingConstants.VALID_COACHING_DAYS.map(day => (
          <EditDayRow
            key={day}
            dayOfWeek={day}
            initialRules={scheduleInput.availabilityRules.filter(r => r.dayOfWeek === day)}
            maxSessions={scheduleInput.maxDailySessions.find(m => m.dayOfWeek === day)?.maxSessions}
            editingMaxSessions={maxDailySessionsEnabled}
            onChangeIntervals={updateScheduleWithNewIntervals}
            onChangeMaxSessions={updateScheduleWithNewMaxSessions}
          />
        ))}
        <Row>
          <Row size="xsmall">
            <Text weight="semibold">Max daily sessions</Text> (optional)
          </Row>
          <Checkbox
            data-test="enable-max-sessions-checkbox"
            label={<Text weight="normal">Set a maximum number of desired sessions per day</Text>}
            checked={maxDailySessionsEnabled}
            onChange={() => {
              setMaxDailySessionsEnabled(!maxDailySessionsEnabled);
            }}
          />
        </Row>
        <Buttons full>
          <Button
            data-test="save-schedule-button"
            disabled={!(timezone && label && startDate)}
            loading={submitting}
            onClick={saveNewSchedule}>
            Set schedule
          </Button>
        </Buttons>
      </Container>
    </>
  );
}

function convertToInputType(
  schedule?: CoachingAvailabilitySchedule
): AvailabilityScheduleInput | undefined {
  return schedule
    ? pick(
        {
          ...schedule,
          availabilityRules: schedule.availabilityRules.map<AvailabilityRuleInput>(r =>
            pick(r, ['uuid', 'startTime', 'endTime', 'dayOfWeek'])
          ),
          maxDailySessions: schedule.maxDailySessions.map<MaxDailySessionsInput>(m =>
            pick(m, ['maxSessions', 'dayOfWeek'])
          ),
        },
        ['uuid', 'label', 'startDate', 'availabilityRules', 'maxDailySessions']
      )
    : undefined;
}
